import React, { useEffect, useMemo, useState } from 'react';
import { X, Loader2, Check } from 'lucide-react';
import { useNivelesQuery } from '../../services/hooks/useNiveles';
import {
    useCrearPlazasEventosMutation,
    useVerificarDisponibilidadPlazas,
    useMoverPlazaMutation,
    useChoquesPlazasQuery,
    esChoquesCambiados
} from '../../services/hooks/usePlazas';
import { ChoquesPlazasAviso, Decisiones, decisionesPorDefecto, idsChoques } from './ChoquesPlazasAviso';
import type { ClaseCompletaResultado } from '../../services/api/plazaService';
import { Plaza } from '../../types/plaza';
import { fetchEventosByDateRangeAndNivel } from '../../services/api/eventoService';
import { toast } from 'react-toastify';
import { useUser } from '@clerk/clerk-react';
import { queryClient } from '../../main';
import { useCotizacionQuery } from '../../services/hooks/useSuscripciones';
import { AvisoPrecio, DesglosePrecio, SelectorTarifas } from '../suscripciones/PrecioSuscripcion';
import type { ResultadoPrecio } from '../../types/suscripciones';

interface AgregarPlazaClienteProps {
    clientId: string;
    suscripcionId?: string;
    onClose: () => void;
    onPlazaAdded?: () => void;
    // M5: when set, the modal works in "move" mode — it edits this existing plaza
    // to the newly-selected slot instead of creating a new one.
    plazaToMove?: Plaza;
    // Plazas activas del cliente, para enseñar cómo queda el importe.
    plazasActuales?: Plaza[];
}

const idNivel = (nivel: Plaza['nivel_id']) => (typeof nivel === 'object' && nivel ? nivel._id || '' : nivel || '');

const euros = (n?: number | null) => (n == null ? '—' : `${n} €`);

// Lo que pasó con el importe tras crear o mover (lo recalcula el back).
const avisarPrecio = (precio?: ResultadoPrecio) => {
    if (!precio) return;
    if (precio.estado === 'recalculada' && precio.importe !== precio.importeAnterior) {
        toast.info(`Importe actualizado: ${euros(precio.importeAnterior)} → ${euros(precio.importe)} (${precio.frecuencia})`, { autoClose: 6000 });
    } else if (precio.estado === 'sin-tarifa') {
        toast.warning('No se pudo recalcular el importe: revisa las tarifas y la suscripción del cliente.', { autoClose: 8000 });
    } else if (precio.estado === 'error') {
        toast.warning('La plaza se guardó, pero no se pudo recalcular el importe. Revisa la suscripción.', { autoClose: 8000 });
    }
};

interface HorarioNivel {
    dia: string;
    hora: string;
    nivel: string;
    frecuencia: number;
}

interface DisponibilidadResultado {
    dia: string;
    horaInicio: string;
    nivel: {
        _id: string;
        nombre: string;
    };
    ocupadas: number;
    disponibles: number;
    plazasActuales: Array<any>;
}

const AgregarPlazaCliente: React.FC<AgregarPlazaClienteProps> = ({
    clientId,
    suscripcionId,
    onClose,
    onPlazaAdded,
    plazaToMove,
    plazasActuales = [],
}) => {
    const { user } = useUser();
    const isMoveMode = !!plazaToMove;

    // Estados
    const [nivelSeleccionado, setNivelSeleccionado] = useState<string>('');
    const [diaSeleccionado, setDiaSeleccionado] = useState<string>('');
    const [horaSeleccionada, setHoraSeleccionada] = useState<string>('');
    const [enEspera, setEnEspera] = useState<boolean>(false);
    const [observaciones, setObservaciones] = useState<string>('');
    const [horariosDisponibles, setHorariosDisponibles] = useState<HorarioNivel[]>([]);
    const [resultadoDisponibilidad, setResultadoDisponibilidad] = useState<DisponibilidadResultado | null>(null);
    const [buscandoHorarios, setBuscandoHorarios] = useState<boolean>(false);
    
    // Consultas y mutaciones
    const { data: niveles, isLoading: isLoadingNiveles } = useNivelesQuery();
    const verificarDisponibilidad = useVerificarDisponibilidadPlazas();
    const crearPlazaMutation = useCrearPlazasEventosMutation();
    const moverPlazaMutation = useMoverPlazaMutation();

    // Precio con la plaza nueva (o movida), calculado por el back. Si el nivel
    // tiene varias tarifas y el cliente no tiene ninguna, se elige aquí.
    const [tarifasElegidas, setTarifasElegidas] = useState<Record<string, string>>({});
    const plazasResultantes = useMemo(() => {
        if (!nivelSeleccionado) return [];
        const actuales = plazasActuales
            .filter((p) => p.activo !== false)
            .map((p) => ({ _id: p._id, nivel_id: idNivel(p.nivel_id) }));
        if (isMoveMode) {
            return actuales.map((p) => ({ nivel_id: p._id === plazaToMove?._id ? nivelSeleccionado : p.nivel_id }));
        }
        return [...actuales.map((p) => ({ nivel_id: p.nivel_id })), { nivel_id: nivelSeleccionado }];
    }, [plazasActuales, nivelSeleccionado, isMoveMode, plazaToMove?._id]);
    const cotizacion = useCotizacionQuery({
        plazas: plazasResultantes,
        tarifasAsignadas: Object.values(tarifasElegidas).filter(Boolean),
        suscripcionId,
        enabled: !!suscripcionId && !!nivelSeleccionado,
    });
    const precioNoDepende = !!(cotizacion.data?.precioFijo || cotizacion.data?.ilimitada);
    const faltaTarifa = !precioNoDepende && !!cotizacion.data && !cotizacion.data.ok && cotizacion.data.faltaElegir.length > 0;
    // const { data: progressData, isSuccess } = usePlazaEventoProgress(creacionSessionId || undefined);

    // Clases ya generadas del horario elegido que están llenas por gente sin
    // plaza (recuperaciones, clases de prueba…): el cliente no entrará en ellas.
    // Solo tiene sentido si la plaza en sí tiene hueco.
    const hayHueco = !!resultadoDisponibilidad && resultadoDisponibilidad.disponibles > 0;
    const plazaBusqueda = useMemo(
        () => (nivelSeleccionado && diaSeleccionado && horaSeleccionada
            ? [{ dia: diaSeleccionado, horaInicio: horaSeleccionada, nivel_id: nivelSeleccionado }]
            : []),
        [nivelSeleccionado, diaSeleccionado, horaSeleccionada]
    );
    const choques = useChoquesPlazasQuery(plazaBusqueda, { clienteId: clientId, enabled: hayHueco });
    const [decisiones, setDecisiones] = useState<Decisiones>({});
    useEffect(() => {
        // Un refetch no debe pisar lo que el operador ya haya marcado.
        if (choques.data) setDecisiones((prev) => ({ ...decisionesPorDefecto(choques.data), ...prev }));
    }, [choques.data]);

    const avisarClasesCompletas = (clases?: ClaseCompletaResultado[]) => {
        if (!clases?.length) return;
        const compensadas = clases.filter((c) => c.compensada).length;
        toast.warning(
            `No entró en ${clases.length} clase${clases.length !== 1 ? 's' : ''} porque estaba${clases.length !== 1 ? 'n' : ''} completa${clases.length !== 1 ? 's' : ''}`
            + (compensadas ? `; se le ${compensadas === 1 ? 'dio 1 recuperación' : `dieron ${compensadas} recuperaciones`}.` : '.'),
            { autoClose: 6000 }
        );
    };

    // Constantes
    const diasSemana = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

    // Función para obtener horarios disponibles según el nivel seleccionado
    const obtenerHorariosNivel = async (nivelId: string) => {
        try {
            setBuscandoHorarios(true);
            // Ventana rodante: se muestran los horarios de las clases ya generadas
            // en los próximos 30 días desde hoy.
            const fechaInicio = new Date();
            const fechaFin = new Date();
            fechaFin.setDate(fechaFin.getDate() + 30);

            const eventos = await fetchEventosByDateRangeAndNivel(
                fechaInicio.toISOString(),
                fechaFin.toISOString(),
                nivelId
            );
    
            // Convertir los eventos a EventoConDia
            const eventosConDia = eventos.map(evento => {
                const fecha = new Date(evento.fecha);
                const dia = diasSemana[fecha.getDay() - 1]; 
                return {
                    ...evento,
                    dia
                };
            });
    
            // Procesamos los eventos para obtener los horarios únicos
            const horarios = eventosConDia.reduce((acc: HorarioNivel[], evento) => {
                const existe = acc.some(h => 
                    h.dia === evento.dia && 
                    h.hora === evento.horaInicio && 
                    h.nivel === evento.nivel_id
                );
    
                if (!existe) {
                    acc.push({
                        dia: evento.dia,
                        hora: evento.horaInicio,
                        nivel: evento.nivel_id,
                        frecuencia: 1
                    });
                }
    
                return acc;
            }, []);
    
            setHorariosDisponibles(horarios);
            setDiaSeleccionado('');
            setHoraSeleccionada('');
            setResultadoDisponibilidad(null);
        } catch (error) {
            console.error('Error al obtener horarios:', error);
            toast.error('Error al obtener los horarios disponibles');
        } finally {
            setBuscandoHorarios(false);
        }
    };

    // Manejar cambio de nivel
    const handleNivelChange = async (nivelId: string) => {
        setNivelSeleccionado(nivelId);
        
        if (nivelId) {
            await obtenerHorariosNivel(nivelId);
        } else {
            setHorariosDisponibles([]);
            setDiaSeleccionado('');
            setHoraSeleccionada('');
            setResultadoDisponibilidad(null);
        }
    };

    // Obtener horas disponibles para un día específico
    const getHorasParaDia = (dia: string) => {
        return horariosDisponibles
            .filter(h => h.dia === dia)
            .sort((a, b) => {
                const horaA = parseInt(a.hora.split(':')[0]);
                const horaB = parseInt(b.hora.split(':')[0]);
                return horaA - horaB;
            })
            .map(h => h.hora);
    };

    // Verificar disponibilidad de la plaza
    const handleVerificarDisponibilidad = async () => {
        if (!nivelSeleccionado || !diaSeleccionado || !horaSeleccionada) {
            toast.warning('Por favor selecciona nivel, día y hora');
            return;
        }

        try {
            const seleccion = [{
                dia: diaSeleccionado,
                horaInicio: horaSeleccionada,
                nivel_id: nivelSeleccionado
            }];

            const disponibilidad = await verificarDisponibilidad.mutateAsync(seleccion);
            
            if (disponibilidad && disponibilidad.length > 0) {
                setResultadoDisponibilidad(disponibilidad[0]);
            } else {
                setResultadoDisponibilidad({
                    dia: diaSeleccionado,
                    horaInicio: horaSeleccionada,
                    nivel: {
                        _id: nivelSeleccionado,
                        nombre: niveles?.find(n => n._id === nivelSeleccionado)?.nombre || ''
                    },
                    ocupadas: 0,
                    disponibles: 5,
                    plazasActuales: []
                });
            }
        } catch (error) {
            console.error('Error al verificar disponibilidad:', error);
            toast.error('Error al verificar disponibilidad');
        }
    };

    // Función para crear la nueva plaza
    const handleCrearPlaza = async () => {
        if (!nivelSeleccionado || !diaSeleccionado || !horaSeleccionada) {
            toast.warning('Por favor selecciona nivel, día y hora');
            return;
        }
    
        if (resultadoDisponibilidad && resultadoDisponibilidad.disponibles <= 0 && !enEspera) {
            toast.warning('No hay plazas disponibles para este horario');
            return;
        }

        if (faltaTarifa) {
            toast.warning('Elige qué tarifa se aplica a este nivel');
            return;
        }
        const tarifaId = tarifasElegidas[nivelSeleccionado] || undefined;

        // M5: move mode — edit the existing plaza to the new slot instead of creating.
        if (isMoveMode && plazaToMove?._id) {
            try {
                const movida = await moverPlazaMutation.mutateAsync({
                    plazaId: plazaToMove._id,
                    dia: diaSeleccionado,
                    horaInicio: horaSeleccionada,
                    nivel_id: nivelSeleccionado,
                    updatedBy: user?.firstName || user?.username || 'Usuario del sistema',
                    tarifaId,
                });
                avisarClasesCompletas(movida.clasesCompletas);
                avisarPrecio(movida.precio);
                queryClient.invalidateQueries({ queryKey: ['client', clientId] });
                queryClient.invalidateQueries({ queryKey: ['eventos'] });
                if (onPlazaAdded) onPlazaAdded();
                onClose();
            } catch (error) {
                console.error('Error al mover la plaza:', error);
                // El toast de error lo maneja el hook
            }
            return;
        }

        try {
            const creada = await crearPlazaMutation.mutateAsync({
                cliente_id: clientId,
                dia: diaSeleccionado,
                horaInicio: horaSeleccionada,
                nivel_id: nivelSeleccionado,
                enEspera: enEspera,
                observaciones: observaciones,
                createdBy: user?.firstName || user?.username || 'Usuario del sistema',
                activo: true,
                // El backend valida la tarifa para el nº de plazas resultante antes
                // de crear, y después recalcula el importe él mismo.
                suscripcionId,
                tarifaId,
                compensaciones: decisiones,
                ...(hayHueco && choques.data ? { choquesVistos: idsChoques(choques.data) } : {}),
            });
            avisarClasesCompletas(creada.clasesCompletas);
            avisarPrecio(creada.precio);

            // Invalidar consultas para asegurar datos actualizados
            queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
            queryClient.invalidateQueries({ queryKey: ['client', clientId] });
            queryClient.invalidateQueries({ queryKey: ['eventos'] });
            
            // Notificar que se agregó la plaza
            if (onPlazaAdded) {
                onPlazaAdded();
            }
            
            // Cerrar modal
            onClose();
    
        } catch (error) {
            // Se llenó alguna clase desde el último refresco: no se ha creado
            // nada. Se refresca el aviso (que avisa de lo nuevo) y se vuelve a guardar.
            if (esChoquesCambiados(error)) {
                toast.info('No se ha guardado todavía: revisa el aviso actualizado y vuelve a pulsar Crear Plaza.', { autoClose: 8000 });
                choques.refetch();
                return;
            }
            console.error('Error al crear la plaza:', error);
            // El toast de error lo maneja el hook
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">{isMoveMode ? 'Mover Plaza' : 'Agregar Nueva Plaza'}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                {/* M5: contexto del slot actual al mover */}
                {isMoveMode && plazaToMove && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-800">
                            <strong>Plaza actual:</strong> {plazaToMove.dia} · {plazaToMove.horaInicio}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                            Selecciona el nuevo nivel, día y hora. El cliente se moverá a las clases futuras del nuevo horario.
                        </p>
                    </div>
                )}

                {/* Selección de nivel */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nivel
                    </label>
                    <select
                        value={nivelSeleccionado}
                        onChange={(e) => handleNivelChange(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                        <option value="">Seleccionar nivel</option>
                        {niveles?.map(nivel => (
                            <option key={nivel._id} value={nivel._id}>
                                {nivel.nombre}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Estado de carga al buscar los horarios del nivel */}
                {nivelSeleccionado && buscandoHorarios && (
                    <div className="mb-4 flex items-center text-sm text-gray-500">
                        <Loader2 className="animate-spin mr-2" size={16} /> Buscando horarios disponibles...
                    </div>
                )}

                {/* Estado vacío: el nivel no tiene clases generadas en la ventana */}
                {nivelSeleccionado && !buscandoHorarios && horariosDisponibles.length === 0 && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-sm text-yellow-700">
                            No hay clases generadas para este nivel en las próximas semanas.
                            Genera el horario de este periodo antes de asignar la plaza.
                        </p>
                    </div>
                )}

                {/* Selección de día */}
                {horariosDisponibles.length > 0 && (
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Día
                        </label>
                        <div className="grid grid-cols-5 gap-2">
                            {diasSemana
                                .filter(dia => horariosDisponibles.some(h => h.dia === dia))
                                .map(dia => (
                                    <button
                                        key={dia}
                                        type="button"
                                        onClick={() => {
                                            setDiaSeleccionado(diaSeleccionado === dia ? '' : dia);
                                            setHoraSeleccionada('');
                                            setResultadoDisponibilidad(null);
                                        }}
                                        className={`py-2 px-3 rounded-md text-center ${
                                            diaSeleccionado === dia
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                        }`}
                                    >
                                        {dia}
                                    </button>
                                ))}
                        </div>
                    </div>
                )}

                {/* Selección de hora */}
                {diaSeleccionado && (
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Hora
                        </label>
                        <div className="grid grid-cols-4 gap-2">
                            {getHorasParaDia(diaSeleccionado).map(hora => (
                                <button
                                    key={hora}
                                    type="button"
                                    onClick={() => {
                                        setHoraSeleccionada(horaSeleccionada === hora ? '' : hora);
                                        setResultadoDisponibilidad(null);
                                    }}
                                    className={`py-2 px-3 rounded-md text-center ${
                                        horaSeleccionada === hora
                                            ? 'bg-blue-500 text-white'
                                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                    }`}
                                >
                                    {hora}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Botón para verificar disponibilidad */}
                {nivelSeleccionado && diaSeleccionado && horaSeleccionada && !resultadoDisponibilidad && (
                    <div className="mb-4">
                        <button
                            onClick={handleVerificarDisponibilidad}
                            disabled={verificarDisponibilidad.isPending}
                            className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                            {verificarDisponibilidad.isPending ? (
                                <Loader2 className="animate-spin mr-2" size={16} />
                            ) : 'Verificar Disponibilidad'}
                        </button>
                    </div>
                )}

                {/* Resultado de disponibilidad */}
                {resultadoDisponibilidad && (
                    <div className={`mb-4 p-4 rounded-lg ${
                        resultadoDisponibilidad.disponibles > 0
                            ? 'bg-green-50 border border-green-200'
                            : 'bg-red-50 border border-red-200'
                    }`}>
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="font-medium">
                                    {resultadoDisponibilidad.dia} - {resultadoDisponibilidad.horaInicio}
                                </p>
                                <p className="text-sm text-gray-600">
                                    Nivel: {resultadoDisponibilidad.nivel.nombre}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className={`font-bold ${
                                    resultadoDisponibilidad.disponibles > 0 
                                        ? 'text-green-600' 
                                        : 'text-red-600'
                                }`}>
                                    {resultadoDisponibilidad.disponibles} plaza{resultadoDisponibilidad.disponibles !== 1 ? 's' : ''} disponible{resultadoDisponibilidad.disponibles !== 1 ? 's' : ''}
                                </p>
                                <p className="text-sm text-gray-500">
                                    ({resultadoDisponibilidad.ocupadas} ocupadas de {resultadoDisponibilidad.ocupadas + Math.max(0, resultadoDisponibilidad.disponibles)})
                                </p>
                            </div>
                        </div>

                        {resultadoDisponibilidad.plazasActuales.length > 0 && (
                            <div className="mt-2">
                                <p className="text-sm text-gray-500 mb-1">
                                    Alumnos actuales:
                                </p>
                                <div className="text-sm text-gray-600 pl-4">
                                    {resultadoDisponibilidad.plazasActuales.map((plaza, idx) => (
                                        <p key={idx}>• {plaza.clienteNombre || 'Cliente sin nombre'}</p>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Opción de lista de espera si no hay disponibilidad */}
                        {resultadoDisponibilidad.disponibles <= 0 && (
                            <div className="mt-3 flex items-center">
                                <input
                                    id="enEspera"
                                    type="checkbox"
                                    checked={enEspera}
                                    onChange={(e) => setEnEspera(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="enEspera" className="ml-2 block text-sm text-gray-700">
                                    Añadir a lista de espera
                                </label>
                            </div>
                        )}
                    </div>
                )}

                {hayHueco && (
                    <ChoquesPlazasAviso
                        choques={choques.data}
                        isLoading={choques.isLoading}
                        isError={choques.isError}
                        decisiones={decisiones}
                        onChange={(eventoId, compensar) => setDecisiones((prev) => ({ ...prev, [eventoId]: compensar }))}
                        nombreNivel={(id) => niveles?.find((n) => n._id === id)?.nombre}
                        soloAviso={isMoveMode}
                    />
                )}

                {/* Precio: cómo queda el importe con esta plaza */}
                {suscripcionId && resultadoDisponibilidad && (resultadoDisponibilidad.disponibles > 0 || enEspera) && (
                    <div className="mb-4 p-3 rounded-md border border-gray-200 bg-gray-50 space-y-2">
                        {precioNoDepende ? (
                            <p className="text-sm text-gray-600">
                                {cotizacion.data?.precioFijo ? 'Este cliente tiene precio fijo' : 'Suscripción ilimitada'}: el importe no cambia.
                            </p>
                        ) : (
                            <>
                                <SelectorTarifas
                                    cotizacion={cotizacion.data}
                                    // Solo el nivel de esta plaza, y solo si el cliente aún no tiene
                                    // tarifa en él: el modal solo envía esa (tarifaId).
                                    nivelesActuales={[nivelSeleccionado]}
                                    soloPendientes
                                    elegidas={tarifasElegidas}
                                    onChange={(nivelId, tarifaId) => setTarifasElegidas((prev) => ({ ...prev, [nivelId]: tarifaId }))}
                                    nombreNivel={(id) => niveles?.find((n) => n._id === id)?.nombre}
                                />
                                {cotizacion.isFetching && <p className="text-xs text-gray-500">Calculando importe…</p>}
                                <AvisoPrecio cotizacion={cotizacion.data} />
                                {cotizacion.data?.ok && (
                                    <>
                                        <p className="text-sm">
                                            Importe: {euros(cotizacion.data.importeActual)} → <strong>{euros(cotizacion.data.total)}</strong>
                                            <span className="text-gray-500"> ({cotizacion.data.frecuencia})</span>
                                        </p>
                                        {cotizacion.data.usaTarifasNombradas && (
                                            <DesglosePrecio desglose={cotizacion.data.desglose} total={cotizacion.data.total} />
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Observaciones */}
                {resultadoDisponibilidad && (resultadoDisponibilidad.disponibles > 0 || enEspera) && (
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Observaciones (opcional)
                        </label>
                        <textarea
                            value={observaciones}
                            onChange={(e) => setObservaciones(e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            rows={3}
                            placeholder="Añade observaciones si es necesario..."
                        />
                    </div>
                )}

                {/* Botones de acción */}
                <div className="flex justify-end gap-3 mt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Cancelar
                    </button>

                    {resultadoDisponibilidad && (resultadoDisponibilidad.disponibles > 0 || enEspera) && (
                        <button
                            type="button"
                            onClick={handleCrearPlaza}
                            disabled={crearPlazaMutation.isPending || moverPlazaMutation.isPending}
                            className="flex items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                        >
                            {(crearPlazaMutation.isPending || moverPlazaMutation.isPending) ? (
                                <Loader2 className="animate-spin mr-2" size={16} />
                            ) : (
                                <Check className="mr-2" size={16} />
                            )}
                            {isMoveMode ? 'Mover Plaza' : 'Crear Plaza'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AgregarPlazaCliente;